import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop } from 'lodash';
import { Input, InputNumber } from 'antd';

import { FormattedMessage } from 'react-intl';
import FieldCheck from 'containers/TemplatePreviewPage/FieldCheck';

import messages from 'containers/TemplatePreviewPage/TemplateFlows/ComplexFlow/messages';

import { FIELD_TYPE } from 'containers/TemplatePreviewPage/constants';
import QuestionHeader from '../QuestionHeader';

const SimpleText = ({
  question,
  onUpdateVariable,
  shouldAutoFocus = false,
  hideErrors = false,
  hideHeader = false,
  skipTopMargin,
  isRepeating = false,
  onEnter = noop,
}) => {
  const { fieldType, value, hasError, isPristine, validations = {} } = question;

  const onChange = useCallback(
    (eventOrValue) => {
      let newValue;
      if (fieldType === FIELD_TYPE.NUMBER) {
        newValue = eventOrValue;
      } else {
        newValue = eventOrValue.target.value;
      }
      onUpdateVariable(newValue);
    },
    [fieldType, onUpdateVariable],
  );

  const isFieldNumber = fieldType === FIELD_TYPE.NUMBER;
  const FieldComponent = isFieldNumber ? InputNumber : Input;

  return (
    <React.Fragment>
      {!hideHeader && (
        <QuestionHeader
          title={question.body}
          note={question.note}
          description={question.description}
          skipTopMargin={skipTopMargin}
        />
      )}
      <div className="w-full">
        <div className="position-relative">
          <FieldComponent
            // eslint-disable-next-line react/no-array-index-key
            className={classNames(
              { 'pf-wizard-input-number': isFieldNumber },
              'w-full',
              'border-grey-5',
              'border-grey-5-hover',
              'border-blue-6-focus',
              'color-grey-8-placeholder',
              'color-grey-10',
              'pf-input-no-shadow',
              'padding-left-sm',
              'ant-input-number-hide-arrows',
              { 'js-repeating-field': isRepeating },
            )}
            onPressEnter={onEnter}
            autoFocus={shouldAutoFocus}
            placeholder={question.placeholder}
            value={value}
            onChange={onChange}
            style={{
              height: '48px',
              lineHeight: '48px',
              paddingRight: '34px',
            }}
          />
          <FieldCheck checked={!hasError} />
        </div>
        {!isPristine && hasError && hasError.required && !hideErrors && (
          <span className="color-red-6">
            <FormattedMessage {...messages.validation.fieldRequired} />
          </span>
        )}
        {!isPristine && hasError && hasError.limitMin && !hideErrors && (
          <span className="color-red-6">
            <FormattedMessage
              {...messages.validation.fieldLimitMin}
              values={{ limitMin: validations.limitMin }}
            />
          </span>
        )}
        {!isPristine && hasError && hasError.limitMax && !hideErrors && (
          <span className="color-red-6">
            <FormattedMessage
              {...messages.validation.fieldLimitMax}
              values={{ limitMax: validations.limitMax }}
            />
          </span>
        )}
        {!isPristine && hasError && hasError.notInteger && !hideErrors && (
          <span className="color-red-6">
            <FormattedMessage {...messages.validation.fieldNotInteger} />
          </span>
        )}
        {!isPristine &&
          hasError &&
          hasError.minMaxRangeInvalidMin &&
          !hideErrors && (
            <span className="color-red-6">
              <FormattedMessage {...messages.validation.vwMin} />
            </span>
          )}
        {!isPristine &&
          hasError &&
          hasError.minMaxRangeInvalidMax &&
          !hideErrors && (
            <span className="color-red-6">
              <FormattedMessage {...messages.validation.vwMax} />
            </span>
          )}
      </div>
    </React.Fragment>
  );
};

SimpleText.propTypes = {
  question: PropTypes.object,
  shouldAutoFocus: PropTypes.bool,
  onUpdateVariable: PropTypes.func.isRequired,
};

export default SimpleText;
